import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Backpack,
  MessageSquare,
  Megaphone,
  X,
  GraduationCap,
  Users,
  School,
  Package,
  Tag,
  TreePalm,
  Puzzle,
  Wallet,
  NotebookPen,
  Newspaper,
  Image as ImageIcon,
  Images,
  Settings,
  UserRound,
  ChevronRight,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { isNavActive, sideNavItems } from "./AdminSidebar";

const DRAWER_GROUPS = [
  {
    title: "Overview",
    paths: ["/"],
  },
  {
    title: "People",
    paths: ["/teachers", "/parents", "/children"],
  },
  {
    title: "School",
    paths: ["/classes", "/holidays", "/activities", "/fees", "/homework"],
  },
  {
    title: "Operations",
    paths: ["/inventory", "/categories", "/posts", "/notifications", "/advertisements", "/gallery", "/support"],
  },
];

const ACCOUNT_ITEMS = [
  { key: "profile", icon: UserRound, label: "Profile", path: "/profile" },
  { key: "settings", icon: Settings, label: "Settings", path: "/settings" },
];

const PATH_ICONS = {
  "/": LayoutDashboard,
  "/teachers": GraduationCap,
  "/parents": Users,
  "/children": Backpack,
  "/classes": School,
  "/inventory": Package,
  "/categories": Tag,
  "/holidays": TreePalm,
  "/activities": Puzzle,
  "/fees": Wallet,
  "/homework": NotebookPen,
  "/posts": Newspaper,
  "/support": MessageSquare,
  "/notifications": Megaphone,
  "/advertisements": ImageIcon,
  "/gallery": Images,
};

function itemForPath(path) {
  if (path === "/") {
    return { key: "1", icon: LayoutDashboard, label: "Dashboard", path: "/" };
  }
  const fromSidebar = sideNavItems.find((item) => item.path === path);
  if (fromSidebar) return fromSidebar;
  if (path === "/gallery") {
    return { key: "15", icon: Images, label: "Gallery", path: "/gallery" };
  }
  return null;
}

function NavListItem({ icon: Icon, label, active, onClick }) {
  return (
    <button
      type="button"
      className={cn("m-nav-row", active && "is-active")}
      onClick={onClick}
    >
      <span className="m-nav-row-icon" aria-hidden>
        <Icon size={15} strokeWidth={1.9} />
      </span>
      <span className="m-nav-row-label">{label}</span>
      <ChevronRight size={14} className="m-nav-row-chevron" aria-hidden />
    </button>
  );
}

export function MobileNavDrawer({ open, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  const go = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <div
      className="m-top-drawer"
      id="admin-mobile-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
    >
      <button type="button" className="m-top-drawer-backdrop" aria-label="Close menu" onClick={onClose} />
      <div className="m-top-drawer-panel">
        <header className="m-top-drawer-head">
          <div className="m-more-brand">
            <span className="m-more-mark is-tree" aria-hidden>
              <img src="/images/splash_tree_icon.png" alt="" />
            </span>
            <div>
              <p className="m-more-kicker">Birchwood Academy</p>
              <h2>Menu</h2>
            </div>
          </div>
          <button type="button" className="m-more-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="m-top-drawer-scroll">
          {DRAWER_GROUPS.map((group) => (
            <section key={group.title} className="m-nav-group">
              <h3>{group.title}</h3>
              <div className="m-nav-list">
                {group.paths.map((path) => {
                  const item = itemForPath(path);
                  if (!item) return null;
                  const Icon = PATH_ICONS[path] || item.icon;
                  return (
                    <NavListItem
                      key={path}
                      icon={Icon}
                      label={item.label}
                      active={isNavActive(path, location.pathname)}
                      onClick={() => go(path)}
                    />
                  );
                })}
              </div>
            </section>
          ))}

          <section className="m-nav-group">
            <h3>Account</h3>
            <div className="m-nav-list">
              {ACCOUNT_ITEMS.map((item) => (
                <NavListItem
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  active={isNavActive(item.path, location.pathname)}
                  onClick={() => go(item.path)}
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export const MobileMoreSheet = MobileNavDrawer;
export const MobileBottomNav = () => null;
